export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? (window.location.port === "3000" ? "http://localhost:4000" : "")).replace(/\/$/, "");

const ADMIN_TOKEN_KEY = "ga_admin_token";

export function getAdminToken(): string {
  return localStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
}

export function setAdminToken(token: string): void {
  if (token.trim()) localStorage.setItem(ADMIN_TOKEN_KEY, token.trim());
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function authHeaders(extra: HeadersInit = {}): HeadersInit {
  const token = getAdminToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function tryRefreshOnUnauthorized(): Promise<boolean> {
  try {
    const auth = await import("./auth");
    if (auth.getAuthMode() !== "cognito") return false;
    const config = await auth.fetchCognitoConfig();
    if (!config.cognitoEnabled) return false;
    const session = await auth.refreshSession(config, true);
    return Boolean(session?.idToken);
  } catch {
    return false;
  }
}

export async function api<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(authHeaders(init.headers ?? {}));
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  let payload: unknown = null;
  try { payload = await response.json(); } catch { /* empty response */ }

  if (response.status === 401 && !retried) {
    const refreshed = await tryRefreshOnUnauthorized();
    if (refreshed) return api<T>(path, init, true);
    const auth = await import("./auth");
    auth.signOut();
  }

  if (!response.ok) {
    const message = payload && typeof payload === "object" && "error" in payload ? String((payload as { error: unknown }).error) : `HTTP ${response.status}`;
    throw new Error(message);
  }
  return payload as T;
}

export function uploadManual(file: File): Promise<unknown> {
  return fetch(`${API_BASE}/api/admin/manuals`, {
    method: "POST",
    headers: authHeaders({
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "X-File-Name": file.name,
    }),
    body: file,
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    return data;
  });
}

export function uploadKnowledgeBase(file: File): Promise<unknown> {
  return fetch(`${API_BASE}/api/admin/faq/import-knowledge-base`, {
    method: "POST",
    headers: authHeaders({
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "X-File-Name": file.name,
    }),
    body: file,
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    return data;
  });
}

/** Download an admin file returned as `{ filename, mimeType, contentBase64 }` (avoids API GW binary corruption). */
export async function downloadAdminFile(path: string, fallbackName: string): Promise<void> {
  const data = await api<{ filename?: string; mimeType?: string; contentBase64: string }>(path);
  if (!data?.contentBase64) throw new Error("Download payload missing file data.");
  const binary = atob(data.contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: data.mimeType || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = data.filename || fallbackName;
  a.click();
  URL.revokeObjectURL(url);
}
